import { NetworkStatus, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FundPostStatusEnumType } from '__generated__/graphql';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import LoadingIndicator from 'primitives/LoadingIndicator';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import CreatePostDialogButton from './CreatePostDialogButton';

const FUND_POSTS_QUERY = gql(`
  query fetchFundPosts($cursor: ID, $limit: Int, $filters: FundPostsFilterType) {
    fundPosts(cursor: $cursor, limit: $limit, filters: $filters) {
      nodes {
        id
        title
        status
        message
        createdAt
        updatedAt
      }
      pageInfo {
        hasNextPage
        cursor
      }
    }
  }
`);

const FundPostsPage: FC = () => {
  const navigate = useNavigate();
  const { loading, error, data, refetch, fetchMore, networkStatus } = useQuery(FUND_POSTS_QUERY, {
    variables: {
      limit: 50,
      filters: {
        statuses: [
          FundPostStatusEnumType.Draft,
          FundPostStatusEnumType.Published,
          FundPostStatusEnumType.UnderReview,
        ],
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const posts = data.fundPosts.nodes;
    const pageInfo = data.fundPosts.pageInfo;

    return (
      <>
        <div>
          <CreatePostDialogButton />
        </div>
        <DataTable
          data={posts}
          searchFields={['title']}
          onClick={post => navigate(`/fund-posts/${post.id}`)}
          columns={[
            {
              label: 'Title',
              fieldName: 'title',
            },
            {
              label: 'Message',
              fieldName: 'message',
              type: 'HTML',
            },
            {
              label: 'Status',
              fieldName: 'status',
              type: 'STATUS',
            },
            {
              label: 'Created At',
              fieldName: 'createdAt',
              type: 'DATETIME',
            },
            {
              label: 'Updated At',
              fieldName: 'updatedAt',
              type: 'DATETIME',
            },
          ]}
          hasNextPage={pageInfo.hasNextPage}
          filterLoading={networkStatus === NetworkStatus.setVariables}
          paginationLoading={loading}
          onLoadMore={() =>
            fetchMore({
              variables: {
                cursor: pageInfo.cursor,
              },
            })
          }
        />
      </>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Fund Posts"
        subTitle="Manage the various fund posts being sent"
      />
      <SidebarLayoutContent>{renderContent()}</SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default FundPostsPage;
